import type { Key } from 'react';
import type { TenantOrder } from '@/mocks/orders';

/**
 * TCR-2497 — mỗi đơn đang tích: bản đơn thấy lần gần nhất, và với từng bộ lọc (không tính
 * trang/sort) mà đơn đã hiện trên danh sách: trang nơi thấy nó lần cuối.
 */
export type SelectionMemo = Record<string, { order: TenantOrder; scopes: Record<string, string> }>;

/**
 * Ghi nhận các đơn đang tích và đang hiển thị ở trang `page` của bộ lọc `scope`; quên các đơn đã
 * bỏ tích. Đơn ghi nhận ở đúng trang này mà nay không còn (vừa bị Huỷ/Xác nhận… nên rời tab) thì
 * không còn tính là thuộc bộ lọc. Chỉ gọi với dữ liệu THẬT của trang (không phải placeholder của
 * bộ lọc cũ). Trả lại đúng object cũ khi không có gì đổi.
 */
export function rememberVisibleSelection(
  memo: SelectionMemo,
  selectedKeys: readonly Key[],
  rows: readonly TenantOrder[],
  scope: string,
  page: string,
): SelectionMemo {
  const selected = new Set(selectedKeys.map(String));
  const visible = new Map(rows.map((row) => [String(row._id), row]));
  let changed = false;
  const next: SelectionMemo = {};
  for (const [id, entry] of Object.entries(memo)) {
    if (!selected.has(id)) {
      changed = true;
      continue;
    }
    if (entry.scopes[scope] === page && !visible.has(id)) {
      const { [scope]: _left, ...scopes } = entry.scopes;
      next[id] = { order: entry.order, scopes };
      changed = true;
    } else {
      next[id] = entry;
    }
  }
  for (const [id, row] of visible) {
    if (!selected.has(id)) continue;
    const entry = next[id];
    if (entry && entry.order === row && entry.scopes[scope] === page) continue;
    next[id] = { order: row, scopes: { ...entry?.scopes, [scope]: page } };
    changed = true;
  }
  return changed ? next : memo;
}

/**
 * Đơn mà badge, panel "Hành động cho N đơn hàng" và các hành động áp dụng: đơn đang tích trên
 * trang hiện tại, cộng đơn đã tích ở trang khác của CHÍNH bộ lọc này. Đơn tích dưới bộ lọc khác
 * mà bộ lọc này đang ẩn vẫn giữ trong vùng chọn nhưng không được tính, cho tới khi hiện lại.
 */
export function effectiveSelection(
  memo: SelectionMemo,
  selectedKeys: readonly Key[],
  rows: readonly TenantOrder[],
  scope: string,
): TenantOrder[] {
  const selected = new Set(selectedKeys.map(String));
  const visible = rows.filter((row) => selected.has(String(row._id)));
  const visibleIds = new Set(visible.map((row) => String(row._id)));
  const offPage = selectedKeys
    .map(String)
    .filter((id) => !visibleIds.has(id) && memo[id]?.scopes[scope] !== undefined)
    .map((id) => memo[id].order);
  return [...visible, ...offPage];
}
